<script setup lang="ts">
import { computed, onBeforeUnmount, ref, watch } from 'vue'
import { storeToRefs } from 'pinia'
import type { VForm } from 'vuetify/components'
import type {
  Calculation,
  CalculationFormPayload,
  HardwareProfile
} from '@/stores/type/calculation.type'
import {
  DEFAULT_VM_MAX_RUNTIME_SECONDS,
  VM_MAX_RUNTIME_OPTIONS
} from '@/stores/type/calculation.type'
import type { ProjectSpending } from '@/stores/type/project.type'
import { useHardwareProfileStore } from '@/stores/hardwareProfile'
import { useCalculationStore } from '@/stores/calculation'

const dialog = defineModel<boolean>({ required: true })
const props = defineProps<{
  item: Calculation
  loading?: boolean
  spending?: ProjectSpending | null
}>()

const emit = defineEmits<{
  submit: [value: CalculationFormPayload]
}>()

const formRef = ref<VForm>()
const isValid = ref(false)
const name = ref('')
const note = ref('')
const hardwareProfileId = ref<number | null>(null)
const containerImage = ref('')
const vmMaxRuntimeSeconds = ref(DEFAULT_VM_MAX_RUNTIME_SECONDS)
const zipFile = ref<File | File[] | null>(null)
const hardwareProfileStore = useHardwareProfileStore()
const calculationStore = useCalculationStore()
const { availableItems: hardwareProfiles, availableLoading: hardwareProfilesLoading } =
  storeToRefs(hardwareProfileStore)
const {
  containerImages,
  containerImagesLoading,
  containerImagesError,
  inputUploadProgress,
  inputUploadMessage,
  inputUploadError
} = storeToRefs(calculationStore)

const selectedZipFile = computed(() => {
  if (Array.isArray(zipFile.value)) return zipFile.value[0] ?? null
  return zipFile.value
})

const sortHardwareProfilesByName = (profiles: HardwareProfile[]) => {
  return [...profiles].sort((left, right) => {
    const nameComparison = left.name.localeCompare(right.name, undefined, {
      sensitivity: 'base',
      numeric: true
    })
    if (nameComparison !== 0) return nameComparison
    return left.id - right.id
  })
}

const hardwareProfileItems = computed(() =>
  sortHardwareProfilesByName(hardwareProfiles.value).map((profile) => ({
    title: hardwareProfileTitle(profile),
    value: profile.id
  }))
)

const containerImageItems = computed(() =>
  containerImages.value.map((image) => ({
    title: image.label,
    value: image.value
  }))
)

const vmMaxRuntimeItems = computed(() =>
  VM_MAX_RUNTIME_OPTIONS.map((option) => ({
    title: option.label,
    value: option.value
  }))
)

const spendingCurrency = computed(() => props.spending?.currency_code ?? 'EUR')

const spendingWarningColor = computed(() => (props.spending?.limit_exceeded ? 'error' : 'warning'))

const spendingWarningLines = computed(() => {
  const spending = props.spending
  if (!spending) {
    return [
      'Project spending is capped separately for one-time compute and monthly storage.',
      'Active calculations are automatically cancelled if either estimate exceeds its cap.'
    ]
  }
  return [
    `Compute is at ${formatMoney(spending.compute_estimated_cost)} of ${formatMoney(
      spending.compute_spending_cap
    )}.`,
    `Storage is at ${formatMoney(spending.storage_estimated_monthly_cost)}/mo of ${formatMoney(
      spending.storage_spending_cap
    )}/mo.`,
    'Active calculations are automatically cancelled if either estimate exceeds its cap.'
  ]
})

const hardwareProfileTitle = (profile: HardwareProfile) => {
  const resourceLabel = `${profile.cpu_cores} cores, ${profile.memory_gb} GB RAM`
  const spotLabel = profile.spot_instance ? ', spot' : ''
  const diskLabel = `${profile.vm_os_disk_size_gb ?? 32} GB OS disk`
  return `${profile.name} (VM, ${profile.vm_size || 'VM size'}, ${resourceLabel}, ${diskLabel}${spotLabel})`
}

const nameRules = [(value: string) => !!value?.trim() || 'Required.']
const hardwareProfileRules = [(value: number | null) => !!value || 'Required.']
const containerImageRules = [(value: string) => !!value || 'Required.']
const vmMaxRuntimeRules = [(value: number | null) => !!value || 'Required.']

const zipRules = computed(() => [
  () =>
    props.item.input_upload_status === 'VALIDATING' ||
    props.item.input_ready ||
    !!selectedZipFile.value ||
    'Required.',
  () => {
    const file = selectedZipFile.value
    return !file || file.name.toLowerCase().endsWith('.zip') || 'Upload a ZIP file.'
  }
])

const resetForm = () => {
  name.value = props.item.name || ''
  note.value = props.item.note || ''
  hardwareProfileId.value = props.item.hardware_profile
  containerImage.value = props.item.container_image || ''
  selectDefaultHardwareProfile()
  selectDefaultContainerImage()
  vmMaxRuntimeSeconds.value =
    Number(props.item.vm_max_runtime_seconds) || DEFAULT_VM_MAX_RUNTIME_SECONDS
  zipFile.value = null
  formRef.value?.resetValidation()
}

const selectDefaultHardwareProfile = () => {
  if (props.item.id && props.item.hardware_profile) {
    hardwareProfileId.value = props.item.hardware_profile
    return
  }
  const selectedProfileExists = hardwareProfileItems.value.some(
    (profile) => profile.value === hardwareProfileId.value
  )
  if (selectedProfileExists) return

  hardwareProfileId.value = hardwareProfileItems.value[0]?.value ?? null
}

const selectDefaultContainerImage = () => {
  if (props.item.id && props.item.container_image) {
    containerImage.value = props.item.container_image
    return
  }
  const selectedImageExists = containerImageItems.value.some(
    (image) => image.value === containerImage.value
  )
  if (selectedImageExists) return

  containerImage.value = containerImageItems.value[0]?.value ?? ''
}

const refreshHardwareProfiles = async () => {
  if (!props.item.project) return
  await hardwareProfileStore.fetchAvailable(props.item.project)
  selectDefaultHardwareProfile()
}

const refreshContainerImages = async () => {
  await calculationStore.fetchContainerImages()
  selectDefaultContainerImage()
}

const formatMoney = (value?: number | null) => {
  if (value === null || value === undefined || Number.isNaN(Number(value))) return '-'
  return new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: spendingCurrency.value,
    maximumFractionDigits: 2
  }).format(Number(value))
}

const close = () => {
  calculationStore.cancelInputUpload()
  dialog.value = false
  resetForm()
}

const submit = async () => {
  const validationResult = await formRef.value?.validate()
  if (!validationResult?.valid) return
  if (!hardwareProfileId.value) return

  emit('submit', {
    project: props.item.project,
    hardwareProfile: hardwareProfileId.value,
    name: name.value.trim(),
    note: note.value,
    containerImage: containerImage.value,
    vmMaxRuntimeSeconds: vmMaxRuntimeSeconds.value,
    zipFile: selectedZipFile.value
  })
}

onBeforeUnmount(() => calculationStore.cancelInputUpload())

watch(
  () => props.item,
  (item, previous) => {
    if (!item.id || item.id !== previous?.id) resetForm()
  },
  { immediate: true }
)

watch(hardwareProfileItems, () => selectDefaultHardwareProfile(), { immediate: true })

watch(containerImageItems, () => selectDefaultContainerImage(), { immediate: true })

watch(
  dialog,
  (isOpen) => {
    if (isOpen) {
      inputUploadError.value = ''
      resetForm()
      void refreshHardwareProfiles()
      void refreshContainerImages()
    } else {
      calculationStore.cancelInputUpload()
    }
  },
  { immediate: true }
)
</script>

<template>
  <v-dialog v-model="dialog" max-width="520" @click:outside="close">
    <v-card class="form-dialog pa-3">
      <v-card-title>Create calculation</v-card-title>

      <v-card-text class="pl-4">
        <v-form ref="formRef" v-model="isValid" @submit.prevent="submit">
          <v-alert class="mb-4" density="compact" :color="spendingWarningColor" variant="tonal">
            <div v-for="line in spendingWarningLines" :key="line">
              {{ line }}
            </div>
          </v-alert>

          <v-text-field
            v-model="name"
            :readonly="Boolean(item.id) || loading"
            color="primary"
            label="Name"
            :rules="nameRules"
            variant="filled"
            required
          />

          <v-textarea
            v-model="note"
            :readonly="Boolean(item.id) || loading"
            auto-grow
            color="primary"
            label="Notes"
            rows="3"
            variant="filled"
          />

          <v-select
            v-model="hardwareProfileId"
            :readonly="Boolean(item.id) || loading"
            color="primary"
            item-title="title"
            item-value="value"
            label="Hardware profile"
            :items="hardwareProfileItems"
            :loading="hardwareProfilesLoading"
            :menu-props="{ zIndex: 2500 }"
            :rules="hardwareProfileRules"
            variant="filled"
            required
          />

          <div class="d-flex align-start">
            <v-select
              v-model="containerImage"
              :readonly="Boolean(item.id) || loading"
              class="flex-grow-1"
              color="primary"
              item-title="title"
              item-value="value"
              label="Calculation image"
              :items="containerImageItems"
              :error-messages="containerImagesError ? [containerImagesError] : []"
              :loading="containerImagesLoading"
              :menu-props="{ zIndex: 2500 }"
              no-data-text="No images loaded from Azure Container Registry"
              :rules="containerImageRules"
              prepend-inner-icon="mdi-docker"
              variant="filled"
              required
            />

            <v-tooltip
              close-delay="500"
              interactive
              location="top"
              max-width="320"
              open-delay="200"
            >
              <template #activator="{ props: tooltipProps }">
                <span
                  v-bind="tooltipProps"
                  aria-label="Why do I need to choose a calculation image?"
                  class="container-image-info-activator ml-3"
                  role="button"
                  tabindex="0"
                >
                  <v-icon color="primary" icon="mdi-information-outline" size="24" />
                </span>
              </template>
              <span>
                The calculation image determines which calculation software runs and which files the
                input ZIP must contain.
              </span>
              <div class="mt-2">
                <a
                  class="text-primary font-weight-medium"
                  href="/docs/calculation-images.html"
                  rel="noopener noreferrer"
                  target="_blank"
                >
                  Learn more about calculation images.
                </a>
              </div>
            </v-tooltip>
          </div>

          <v-select
            v-model="vmMaxRuntimeSeconds"
            :readonly="Boolean(item.id) || loading"
            color="primary"
            item-title="title"
            item-value="value"
            label="Maximum VM runtime"
            :items="vmMaxRuntimeItems"
            :menu-props="{ zIndex: 2500 }"
            :rules="vmMaxRuntimeRules"
            prepend-inner-icon="mdi-timer-outline"
            variant="filled"
            required
          />

          <v-file-input
            v-model="zipFile"
            accept=".zip,application/zip,application/x-zip-compressed"
            color="primary"
            label="ZIP file"
            prepend-icon=""
            prepend-inner-icon="mdi-folder-zip-outline"
            :rules="zipRules"
            variant="filled"
            :disabled="loading || item.input_upload_status === 'VALIDATING'"
            required
            show-size
          />
        </v-form>
        <v-alert v-if="inputUploadError" type="error" variant="tonal" class="mt-3">
          {{ inputUploadError }}
        </v-alert>
        <div v-if="loading" class="mt-3" role="status" aria-live="polite">
          <div class="mb-2">{{ inputUploadMessage }}</div>
          <v-progress-linear
            :model-value="inputUploadProgress"
            :indeterminate="!inputUploadMessage.startsWith('Uploading')"
            color="primary"
            height="6"
          />
          <div v-if="inputUploadMessage.startsWith('Uploading')" class="mt-1">
            {{ inputUploadProgress }}%
          </div>
        </div>
      </v-card-text>

      <v-card-actions>
        <v-spacer />
        <v-btn class="text-none" color="primary" @click="close">{{
          loading ? 'Continue later' : 'Cancel'
        }}</v-btn>
        <v-btn
          class="text-none"
          color="primary"
          :loading="loading"
          :disabled="!isValid"
          @click="submit"
        >
          {{ item.id ? 'Continue' : 'Save' }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<style scoped>
.container-image-info-activator {
  align-items: center;
  cursor: pointer;
  display: inline-flex;
  flex: 0 0 24px;
  height: 24px;
  justify-content: center;
  margin-top: 16px;
}
</style>
